import {
  AbstractControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { PORTFOLIO_TYPE_SM_MAP_WO_IMAGE } from '@modules/edit-preference/components/portfolio/portfolio.contants';
import { CurrencyPipe } from '@angular/common';
import { Fields } from '@constants/constant';
import get from 'lodash/get';
import set from 'lodash/set';
import { CurrencyToLocal } from '@shared/pipes/convertCurrencyToLocal.pipe';
import { Application } from '@enums/application.enums';
import { CURRENCY_CONVERSION_LABELS } from '@components/currency-conversion-modal/currency-conversion.constants';
import { LOYALTY_POINTS_TEXT } from '@wallet/component/loyalty-points/loyalty-points.constants';
import { POST_PRICE_LOYALTY } from '@modules/dashboard/components/new-post/newposts/constants';

const currencyPipe = new CurrencyPipe('en-US');
const currencyToLocal = new CurrencyToLocal(new CurrencyPipe('en-US'));
export const LOYALTY_POINTS_PAYMENT_ICON_PATH = POST_PRICE_LOYALTY.POINT_COIN_ICON_PATH;

type LoyaltyPointsDisplayOptions = {
  dataKey?: string;
  prefixImageKey?: string;
  prefixImageAltKey?: string;
  loyaltyPointsFlagPath?: string;
  loyaltyPointsAmountPath?: string;
};

export function toPascalCase(str) {
  return str
    .split(' ')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
}

export function toPascalCaseWithSpace(input: string): string {
  if (!input) return '';
  return input
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
export function matchAccountNumbers(
  accountField: string,
  confirmField: string
): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const account = formGroup.get(accountField);
    const confirm = formGroup.get(confirmField);

    if (!account || !confirm) return null;

    if (account.value !== confirm.value) {
      confirm.setErrors({ ...confirm.errors, mismatch: true });
    } else if (confirm.errors?.mismatch) {
      const { mismatch, ...otherErrors } = confirm.errors;
      confirm.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
    }

    return null;
  };
}

export function processVideoFile(videoFile: File): {
  file: string;
  videoFile: File;
} {
  const isMOV =
    videoFile.name.toLowerCase().endsWith('.mov') &&
    videoFile.type === 'video/quicktime';

  if (isMOV) {
    const newFileName = videoFile.name.replace(/\.mov$/i, '.mp4');
    const newVideoFile = new File([videoFile], newFileName, {
      type: 'video/mp4',
      lastModified: videoFile.lastModified
    });
    const newBlobUrl = URL.createObjectURL(newVideoFile);
    return {
      file: newBlobUrl,
      videoFile: newVideoFile
    };
  }

  const originalBlobUrl = URL.createObjectURL(videoFile);
  return {
    file: originalBlobUrl,
    videoFile
  };
}

export function getSelectedTopicTags(form: UntypedFormGroup) {
  const arr: any = [];
  const topicTagList = form?.get('topicTagList');
  if (topicTagList) {
    topicTagList.value.forEach((ele: any) => {
      if (ele.isSelected) {
        arr.push({
          sk: ele.sk,
          pk: ele.pk
        });
      }
    });
  }
  return arr;
}

export function getSelectedHashTag(form: UntypedFormGroup) {
  const arr: any = [];
  const hashTagList = form?.get('hashTagList');
  if (hashTagList) {
    hashTagList.value.forEach((ele: any) => {
      if (ele.isSelected) {
        arr.push({
          sk: ele.sk,
          pk: ele.pk
        });
      }
    });
  }
  return arr;
}

export function precessPortfolioFeedData(data: any) {
  if (data?.length) {
    return data.map((post: any) => ({
      ...post,
      description: PORTFOLIO_TYPE_SM_MAP_WO_IMAGE.includes(post.type)
        ? post.description +
          (post.hashtags?.length
            ? ` #${post.hashtags.map((h: any) => h.name).join(' #')}`
            : '')
        : post.description,
      user_tags: PORTFOLIO_TYPE_SM_MAP_WO_IMAGE.includes(post.type)
        ? post.hashtags.map((h: any) => h.name)
        : post.user_tags || []
    }));
  }
  return [];
}

export const formatCurrencyListingData = (
  data,
  exchangeRateFieldPath: string = 'currency_info.exchange_rate_to_usd',
  symbolFieldPath: string = 'currency_info.currency_symbol',
  dataKey: string = 'actual_payment',
  localPriceKey: string = 'currency_info.currency_price'
) => {
  if (data?.length) {
    return data.map((item: any) => {
      if (get(item, 'is_loyalty_points_payment')) {
        return setLoyaltyPointsPaymentDisplay(item, { dataKey });
      }

      const symbol = get(item, symbolFieldPath);
      const exchangeRate = get(item, exchangeRateFieldPath);
      const actualAmount = get(item, dataKey);
      const localPrice = get(item, localPriceKey, null);

      const amount = localPrice ? localPrice : (exchangeRate || Application.ONE) * actualAmount;
      set(
        item,
        dataKey,
        currencyPipe.transform(amount, symbol || Fields.DOLLAR_SYMBOL)
      );
      return item;
    });
  }
  return [];
};

export const setLoyaltyPointsPaymentDisplay = (
  item: any,
  options: LoyaltyPointsDisplayOptions = {}
) => {
  const {
    dataKey = 'actual_payment',
    prefixImageKey = `${dataKey}_prefix_image_url`,
    prefixImageAltKey = `${dataKey}_prefix_image_alt`,
    loyaltyPointsFlagPath = 'is_loyalty_points_payment',
    loyaltyPointsAmountPath = 'loyalty_points_amount'
  } = options;

  if (!get(item, loyaltyPointsFlagPath)) {
    return item;
  }

  set(item, dataKey, `${get(item, loyaltyPointsAmountPath) ?? ''}`);
  set(item, prefixImageKey, LOYALTY_POINTS_PAYMENT_ICON_PATH);
  set(item, prefixImageAltKey, LOYALTY_POINTS_TEXT.COIN_ALT);

  return item;
};

export const formatDollarIntCurrencyAmount = (
  amount: number,
  sign: string = '',
  currencySymbol: string,
  rate?: number | null,
  showLocalCurrencyOnly: boolean = false
): string => {
  const dollarValue = currencyPipe.transform(amount);
  if (rate && currencySymbol !== Fields.DOLLAR_SYMBOL) {
    const localValue = currencyToLocal.transform(amount, rate, currencySymbol);
    return sign
      ? `${sign}${localValue} (${dollarValue})`
      : showLocalCurrencyOnly
        ? `${localValue}`
        : `${localValue} (${dollarValue})`;
  }

  return sign ? `${sign}${dollarValue}` : `${dollarValue}`;
};

export const roundToNiceNumber = (
  value: number,
  minValue: number = 0
): number => {
  if (value <= 0) return 0;

  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  let rounded = value;

  if (magnitude >= 1000) {
    rounded = Math.round(value / 500) * 500;
  } else if (magnitude >= 100) {
    rounded = Math.round(value / 50) * 50;
  } else if (magnitude >= 10) {
    rounded = Math.round(value / 10) * 10;
  } else {
    rounded = Math.round(value / 5) * 5;
  }

  return Math.max(rounded, minValue);
};

export const calculatePredefinedCurrencies = (
  loggedInUserData: any,
  predefinedTips: any
) => {
  if (loggedInUserData?.currency_info && loggedInUserData?.currency_info?.currency_code != CURRENCY_CONVERSION_LABELS.DOLLAR_SIGN) {
    const rate =
      loggedInUserData?.currency_info?.exchange_rate_to_usd || Application.ONE;

    predefinedTips = predefinedTips.map((item: any) => {
      if (!item) return Application.ZERO.toString();

      const converted = Number(item) * rate;
      const minLocal = Math.round(rate) + Application.ONE;

      const rounded = roundToNiceNumber(converted, minLocal);

      return rounded.toString();
    });

    return predefinedTips;
  }
  return predefinedTips;
};

export const intSymbolRate = (loggedInUserData: any) => {
  const rate =
    loggedInUserData?.currency_info?.exchange_rate_to_usd || Application.ONE;
  const symbol =
    loggedInUserData?.currency_info?.currency_symbol || Fields.DOLLAR_SYMBOL;

  const min = Math.round(1 * rate) || Application.ONE;
  const max = Math.round(10000 * rate) || Application.TEN_THOUSAND;

  return { min, max, symbol };
};

export const isCurrencyFormatted = (val: any): boolean => {
  return typeof val === 'string' && /[^\d.,-]/.test(val);
}

export const makeDescriptionHyperlinkActive = (text: string): string => {
  const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g;

  return text.replace(urlRegex, (url) => {
    const href = url.startsWith('http') ? url : `https://${url}`;
    return `<a class="hyperlink" href="${href}" target="_blank">${url}</a>`;
  });
}